import React, { useState } from 'react';
import PropTypes from 'prop-types';
import urlJoin from 'url-join';
import striptags from 'striptags';
import { createGlobalStyle } from 'styled-components';
import { getFrameAttrs } from 'utils/embed';
import { logError } from 'utils/logging';
import { GlobalContext } from 'globalContext';
import { Link } from 'react-router-dom';
import ResizeDetector from 'react-resize-detector';
import { themeColors, ThemeProvider, FullscreenProvider, PlayerHeader, PlayerFooter, Modal, ShareSheet } from 'msp-components';
import ErrorBoundary from 'components/ErrorBoundary';
import styles from './Wrapper.module.scss';

const getLinkColor = props => {
  if (props.brandColor) {
    return props.brandColor;
  } else {
    return props.theme === 'dark' ? themeColors.themeDarkPrimary : themeColors.themeLightPrimary;
  }
};

const GlobalStyle = createGlobalStyle`
  a,
  a:link,
  a:visited,
  a:focus,
  a:hover {
    color: ${props => getLinkColor(props)};
  }
`;

const Wrapper = ({ project, templateOptions, navigation, titlePaths, children, onContainerResize = () => {} }) => {
  const { theme, accentColor, logo, footerText, sponsorshipText, sponsorshipLink, sponsorshipImage } = templateOptions;

  const { isEmbedded, parentUrl } = getFrameAttrs();
  const { internalDomains } = project || {};

  let isInternalEmbed = false;
  const embedUrl = urlJoin(window.location.origin, window.BASE_PATH);
  if (isEmbedded && parentUrl && internalDomains && internalDomains.length) {
    try {
      const url = new URL(parentUrl);
      if (internalDomains.includes(url.hostname)) {
        isInternalEmbed = true;
      }
    } catch (e) {
      console.error(e);
      logError(e);
    }
  }

  const [showShareSheet, setShowShareSheet] = useState(false);
  const toggleShareSheet = () => setShowShareSheet(!showShareSheet);

  const renderHeaderImage = () => {
    const node = logo ? <img src={logo} style={{ display: 'block', height: 30 }} alt={project.publisher.name} /> : <h1>{project.publisher.name}</h1>;
    return <Link to="/">{node}</Link>;
  };

  const header = isInternalEmbed ? (
    !navigation && <div style={{ height: 1, flex: '0 0 1px', backgroundColor: themeColors.grayNickel }} />
  ) : (
    <PlayerHeader
      imageUrl={logo}
      renderImage={renderHeaderImage}
    >
      {sponsorshipImage && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {!!sponsorshipText && <span style={{ marginRight: 10 }}>{sponsorshipText}</span>}
          {sponsorshipLink ? (
            <a href={sponsorshipLink} target="_blank" rel="noopener noreferrer">
              <img style={{ display: 'block', maxHeight: 40 }} src={sponsorshipImage} alt="sponsorship" />
            </a>
          ) : (
            <img style={{ display: 'block', maxHeight: 40 }} src={sponsorshipImage} alt="sponsorship" />
          )}
        </div>
      )}
    </PlayerHeader>
  );

  return (
    <ThemeProvider theme={theme} brandColor={accentColor || undefined}>
      <GlobalStyle theme={theme} brandColor={accentColor || undefined} />
      <GlobalContext.Provider value={{ titlePaths, isInternalEmbed, embedUrl, toggleShareSheet, showShareSheet }}>
        <FullscreenProvider>
          <div className={styles.container}>
            <ResizeDetector
              handleHeight
              onResize={onContainerResize}
              refreshMode="debounce"
              refreshRate={500}
            />
            {header}
            {navigation}
            <div className={styles.titleContainer}>
              <ErrorBoundary>
                {children}
              </ErrorBoundary>

              <Modal
                overlayStyle={{
                  position: 'fixed',
                  zIndex: 1,
                }}
                isOpen={showShareSheet}
                onRequestClose={() => setShowShareSheet(false)}
              >
                <div style={{ padding: '15px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShareSheet
                    shareUrl={project.shareUrl || embedUrl}
                    title={project.name}
                    description={striptags(project.shortDescription)}
                    publisher={project.publisher}
                    embedUrl={embedUrl}
                    onShareClick={toggleShareSheet}
                    allowEmbed
                  />
                </div>
              </Modal>
            </div>
            {!isInternalEmbed && <PlayerFooter content={footerText} />}
          </div>
        </FullscreenProvider>
      </GlobalContext.Provider>
    </ThemeProvider>
  );
};

Wrapper.propTypes = {
  templateOptions: PropTypes.shape({
    accentColor: PropTypes.string,
    logo: PropTypes.string,
    theme: PropTypes.oneOf(['light', 'dark']),
    footerText: PropTypes.string,
    sponsorshipText: PropTypes.string,
    sponsorshipLink: PropTypes.string,
    sponsorshipImage: PropTypes.string,
  }),
  project: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    socialImage: PropTypes.string,
    analyticsProvider: PropTypes.string,
    analyticsId: PropTypes.string,
    shareUrl: PropTypes.string,
    internalDomains: PropTypes.arrayOf(PropTypes.string),
    publisher: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      twitter: PropTypes.string,
      facebook: PropTypes.string,
    }),
  }),
  titlePaths: PropTypes.object,
  navigation: PropTypes.node,
  children: PropTypes.node,
  onContainerResize: PropTypes.func,
};

export default Wrapper;
